import { Injectable, signal } from '@angular/core';

/**
 * Notification type.
 */
export type NotificationType = 'success' | 'error' | 'warning' | 'info';

/**
 * Notification model.
 */
export interface Notification {
  id: string;
  type: NotificationType;
  message: string;
  title?: string;
  duration?: number;
}

/**
 * Service for managing toast notifications.
 * CPNotes: Provides reactive notification system using signals
 */
@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private readonly _notifications = signal<Notification[]>([]);
  
  readonly notifications = this._notifications.asReadonly();

  /**
   * Shows a success notification.
   */
  success(message: string, title?: string, duration: number = 5000): void {
    this.show('success', message, title, duration);
  }

  /**
   * Shows an error notification.
   */
  error(message: string, title?: string, duration: number = 7000): void {
    this.show('error', message, title, duration);
  }

  /**
   * Shows a warning notification.
   */
  warning(message: string, title?: string, duration: number = 6000): void {
    this.show('warning', message, title, duration);
  }

  /**
   * Shows an info notification.
   */
  info(message: string, title?: string, duration: number = 5000): void {
    this.show('info', message, title, duration);
  }

  /**
   * Shows a notification.
   */
  show(type: NotificationType, message: string, title?: string, duration: number = 5000): void {
    const notification: Notification = {
      id: this.generateId(),
      type,
      message,
      title,
      duration
    };

    this._notifications.update(notifications => [...notifications, notification]);

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(notification.id);
      }, duration);
    }
  }

  /**
   * Removes a notification.
   */
  remove(id: string): void {
    this._notifications.update(notifications => 
      notifications.filter(n => n.id !== id)
    );
  }

  /**
   * Clears all notifications.
   */
  clear(): void {
    this._notifications.set([]);
  }

  /**
   * Generates a unique ID for notifications.
   */
  private generateId(): string {
    return `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }
}

